import { useEffect, useRef } from 'react';
import { useToast } from './use-toast';

/*
 * useIdleLogout
 * Auto-logs the user out after `timeoutMs` of inactivity (default 3 minutes).
 * A warning toast is shown `warningMs` before the forced logout so users have
 * a chance to bring the app back to focus.
 *
 * Only activates when `enabled` is true (i.e., when a user is logged in).
 * Listens to mousemove / mousedown / keydown / touchstart / scroll / wheel
 * across the entire window. Each event resets both timers. We also reset on
 * `visibilitychange` returns so that tab-switches don't unfairly count as idle.
 */
const IDLE_MS = 3 * 60 * 1000;       // 3 minutes
const WARN_BEFORE_MS = 30 * 1000;    // warn at 2m30s

export const useIdleLogout = ({ enabled, onLogout, timeoutMs = IDLE_MS, warningMs = WARN_BEFORE_MS }) => {
  const { toast } = useToast();
  const logoutTimerRef = useRef(null);
  const warnTimerRef = useRef(null);
  const warnedRef = useRef(false);

  useEffect(() => {
    if (!enabled) return undefined;

    const clear = () => {
      if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
      if (warnTimerRef.current) clearTimeout(warnTimerRef.current);
    };

    const schedule = () => {
      clear();
      warnedRef.current = false;
      warnTimerRef.current = setTimeout(() => {
        warnedRef.current = true;
        toast({
          title: 'You are about to be logged out',
          description: `For your security, you will be signed out in ${Math.round(warningMs / 1000)}s due to inactivity. Move your mouse or press a key to stay signed in.`,
          variant: 'destructive',
        });
      }, Math.max(timeoutMs - warningMs, 0));
      logoutTimerRef.current = setTimeout(() => {
        toast({
          title: 'Signed out',
          description: 'You were signed out automatically after 3 minutes of inactivity.',
        });
        onLogout();
      }, timeoutMs);
    };

    const reset = () => { schedule(); };

    // Throttle: at most one reset every 500ms to avoid timer churn on mousemove.
    let last = 0;
    const throttledReset = () => {
      const now = Date.now();
      if (now - last < 500) return;
      last = now;
      reset();
    };

    const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'wheel'];
    events.forEach((e) => window.addEventListener(e, throttledReset, { passive: true }));
    const onVis = () => { if (document.visibilityState === 'visible') reset(); };
    document.addEventListener('visibilitychange', onVis);

    schedule();

    return () => {
      events.forEach((e) => window.removeEventListener(e, throttledReset));
      document.removeEventListener('visibilitychange', onVis);
      clear();
    };
  }, [enabled, onLogout, timeoutMs, warningMs, toast]);
};

export default useIdleLogout;
